import { useEffect, useRef, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { redirectToAuthIfRequired } from "@/lib/auth-required-redirect";
import { useI18n } from "@/lib/i18n";
import { MCP_COPY } from "@/lib/mcp-copy";
import { MCP_PERMISSION_COPY } from "@/lib/mcp-permission-copy";
import { listMcpConnections, revokeMcpConnection } from "@/lib/mcp-api";
import { mcpAccountLabel } from "@/lib/mcp-connections";

export default function McpConnectionsSection() {
  const { locale } = useI18n();
  const copy = MCP_COPY[locale];
  const permissionCopy = MCP_PERMISSION_COPY[locale];
  const navigate = useNavigate();
  const location = useLocation();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [disconnecting, setDisconnecting] = useState("");
  const busy = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setError("");
    listMcpConnections().then((result) => {
      if (!cancelled) setPayload(result);
    }).catch((failure) => {
      if (cancelled || redirectToAuthIfRequired(failure, { navigate, location })) return;
      if (failure?.status === 404) { setPayload({ enabled: false }); return; }
      setPayload({ enabled: true, connections: [], accounts: [] });
      setError(copy.managementError);
    });
    return () => { cancelled = true; };
  }, [attempt, copy, location, navigate]);

  async function disconnect(id) {
    if (busy.current) return;
    busy.current = true;
    setDisconnecting(id);
    setError("");
    try {
      await revokeMcpConnection(id);
      setPayload((current) => ({ ...current, connections: current.connections.filter((connection) => connection.id !== id) }));
    } catch (failure) {
      if (!redirectToAuthIfRequired(failure, { navigate, location })) setError(copy.revokeError);
    } finally {
      busy.current = false;
      setDisconnecting("");
    }
  }

  if (payload?.enabled !== true) return null;
  const connections = (payload.connections || []).filter((connection) => connection.status === "active");
  const accounts = new Map((payload.accounts || []).map((account) => [account.account_id, account]));
  return (
    <section className="ph-no-capture ph-mask rounded-3xl border bg-card p-6" aria-labelledby="mcp-connections-title">
      <div className="flex items-center gap-3"><ShieldCheck className="h-6 w-6 text-primary" aria-hidden="true" /><h2 id="mcp-connections-title" className="font-display text-2xl font-bold">{copy.connections}</h2></div>
      <p className="mt-2 text-sm text-muted-foreground">{copy.management}</p>
      {error ? <div role="alert" className="mt-4 text-sm text-destructive"><p>{error}</p><Button variant="outline" className="mt-3 h-11 rounded-xl" disabled={Boolean(disconnecting)} onClick={() => setAttempt((value) => value + 1)}>{copy.retry}</Button></div> : null}
      {!connections.length && !error ? <p className="mt-5 text-sm text-muted-foreground">{copy.empty}</p> : null}
      <div className="mt-5 space-y-3">
        {connections.map((connection) => (
          <div key={connection.id} className="flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0"><p className="break-words font-semibold">{connection.name || "ChatGPT"}</p>
              <p className="mt-1 break-words text-sm text-muted-foreground">{connection.grant_profile === "min" ? copy.managementConnection
                : connection.grant_profile === "unified" ? permissionCopy.connection : null}</p>
              {connection.grant_profile !== "min" ? <p className="mt-1 break-words text-sm text-muted-foreground">{connection.account_scope === "all_owned"
                ? `${permissionCopy.allOwned}. ${permissionCopy.allOwnedHelp}`
                : (connection.account_ids || []).map((id) => mcpAccountLabel(accounts.get(id), copy.number)).join(" · ")}</p> : null}
            </div>
            <Button variant="outline" className="h-11 rounded-xl" disabled={Boolean(disconnecting)} aria-label={`${copy.disconnect} ${connection.name || "ChatGPT"}`} onClick={() => void disconnect(connection.id)}>{disconnecting === connection.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}{disconnecting === connection.id ? copy.disconnecting : copy.disconnect}</Button>
          </div>
        ))}
      </div>
    </section>
  );
}
